import type { Language, WeekStartDay } from '../types';

const STORAGE_KEY = 'mealPai_language';
const SUPPORTED_LANGUAGES: Language[] = ['en', 'he', 'es', 'ar', 'de', 'fr'];

function isSupportedLanguage(code: string): code is Language {
    return SUPPORTED_LANGUAGES.includes(code as Language);
}

export function getPersistedAppLanguage(): Language | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && isSupportedLanguage(stored)) {
            return stored;
        }
    } catch {
        // ignore
    }
    return null;
}

export function persistAppLanguage(language: Language | undefined | null): void {
    if (!language || !isSupportedLanguage(language)) return;
    try {
        localStorage.setItem(STORAGE_KEY, language);
    } catch {
        // ignore
    }
}

export function isIsraelOrHebrewPreferred(): boolean {
    // 1. Check preferred languages list
    const languages = navigator.languages || [];
    const hasHebrewPref = languages.some(lang => lang.toLowerCase().startsWith('he'));
    if (hasHebrewPref) return true;

    // 2. Check timezone
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone) {
            const tzLower = timezone.toLowerCase();
            if (tzLower.includes('jerusalem') || tzLower.includes('tel_aviv') || tzLower.includes('israel')) {
                return true;
            }
        }
    } catch {
        // ignore
    }

    return false;
}

export function detectBrowserLanguage(): Language {
    // If user prefers Hebrew or is physically in Israel timezone, default to Hebrew.
    if (isIsraelOrHebrewPreferred()) {
        return 'he';
    }

    const code = (navigator.language || 'en').split('-')[0]?.toLowerCase() ?? 'en';
    return isSupportedLanguage(code) ? code : 'en';
}

export function getStartupLanguage(): Language {
    const persisted = getPersistedAppLanguage();
    if (persisted) return persisted;

    return detectBrowserLanguage();
}

export function detectBrowserWeekStartDay(): WeekStartDay {
    // Israel starts the week on Sunday
    if (isIsraelOrHebrewPreferred()) {
        return 'Sunday';
    }

    try {
        const locale = new Intl.Locale(navigator.language || 'en-US');
        // weekInfo.firstDay: 1=Mon, 7=Sun, 6=Sat (Intl spec)
        const firstDay = (locale as any).weekInfo?.firstDay ?? (locale as any).getWeekInfo?.()?.firstDay;
        if (firstDay === 7 || firstDay === 6) return 'Sunday';
    } catch {
        // fall through to default
    }
    return 'Monday';
}
